import React from "react"
import type { GrammarSlide } from "./grammar_glossary"

interface IrregularVerb {
  /** The verb root / imperative, e.g. "déan". */
  root: string
  meaning: string
  present: string
  past: string
  future: string
  verbalNoun: string
}

// The eleven irregular verbs, in their independent forms (the ones used in a plain statement). The past
// commonly adds a d' or a séimhiú, and a few change again in the dependent form after ní / an / go
// (e.g. faigh: future gheobhaidh, dependent bhfaighidh). Standard textbook forms; Ulster uses cluin for
// clois. Kept as reference content here (canonical, not per-word codex data).
const IRREGULAR_VERBS: IrregularVerb[] = [
  { root: "bí", meaning: "be", present: "tá", past: "bhí", future: "beidh", verbalNoun: "bheith" },
  { root: "abair", meaning: "say", present: "deir", past: "dúirt", future: "déarfaidh", verbalNoun: "rá" },
  { root: "beir", meaning: "bear, catch", present: "beireann", past: "rug", future: "béarfaidh", verbalNoun: "breith" },
  { root: "clois", meaning: "hear", present: "cloiseann", past: "chuala", future: "cloisfidh", verbalNoun: "cloisteáil" },
  { root: "déan", meaning: "do, make", present: "déanann", past: "rinne", future: "déanfaidh", verbalNoun: "déanamh" },
  { root: "faigh", meaning: "get", present: "faigheann", past: "fuair", future: "gheobhaidh", verbalNoun: "fáil" },
  { root: "feic", meaning: "see", present: "feiceann", past: "chonaic", future: "feicfidh", verbalNoun: "feiceáil" },
  { root: "ith", meaning: "eat", present: "itheann", past: "d'ith", future: "íosfaidh", verbalNoun: "ithe" },
  { root: "tabhair", meaning: "give", present: "tugann", past: "thug", future: "tabharfaidh", verbalNoun: "tabhairt" },
  { root: "tar", meaning: "come", present: "tagann", past: "tháinig", future: "tiocfaidh", verbalNoun: "teacht" },
  { root: "téigh", meaning: "go", present: "téann", past: "chuaigh", future: "rachaidh", verbalNoun: "dul" },
]

const COLUMNS = ["Verb", "Present", "Past", "Future", "Verbal noun"]

/** The irregular-verb reference chart: one row per verb, the key tense forms across the columns. */
export const IrregularVerbTable: React.FC = () => (
  <div className="overflow-x-auto">
    <table
      className="w-full border-collapse text-sm"
      data-testid="irregular-verb-table"
    >
      <thead>
        <tr className="text-left text-xs uppercase tracking-wide text-secondary-500">
          {COLUMNS.map((col) => (
            <th key={col} className="py-1.5 pr-3 font-medium">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {IRREGULAR_VERBS.map((verb) => (
          <tr
            key={verb.root}
            className="border-t border-secondary-100 align-top"
          >
            <td className="whitespace-nowrap py-1.5 pr-3">
              <span className="font-semibold italic">{verb.root}</span>
              <span className="block text-xs text-muted-foreground">
                {verb.meaning}
              </span>
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 italic">
              {verb.present}
            </td>
            <td className="whitespace-nowrap py-1.5 pr-3 italic">{verb.past}</td>
            <td className="whitespace-nowrap py-1.5 pr-3 italic">
              {verb.future}
            </td>
            <td className="whitespace-nowrap py-1.5 italic">
              {verb.verbalNoun}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="mt-2 text-xs text-muted-foreground">
      Independent (statement) forms. The dependent form after ní, an, or go
      differs for a few (faigh: gheobhaidh, dependent bhfaighidh). Ulster uses
      cluin for clois.
    </p>
  </div>
)

/**
 * A ready-made slide for the conjugation chapter: the full irregular-verb chart with a short, honest
 * lead-in (built in this .tsx so the JSX chart can live on the slide `content`; grammar_glossary.ts
 * imports the finished slide).
 */
export const irregularVerbsSlide: GrammarSlide = {
  heading: "The eleven irregular verbs",
  tip: "grug not learn all eleven at once. grug learn tá, bhí, beidh first, use every day, add rest slow.",
  body:
    "About eleven verbs don't follow either conjugation, so you learn each one. Learn the everyday ones first, like tá and rinne, and add the rest as you meet them.",
  content: <IrregularVerbTable />,
}
